import React from "react";
import { cn } from "../../utils/cn";

const Input = React.forwardRef(({
    className,
    type = "text",
    label,
    description,
    error,
    required = false,
    id,
    ...props
}, ref) => {
    // Generate unique ID if not provided
    const inputId = id || `input-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Modern base input classes with soft borders and enhanced focus states
    const baseInputClasses = "flex h-11 w-full rounded-xl border-2 border-border bg-input px-4 py-3 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-smooth";

    // Modern checkbox with rounded styling
    if (type === "checkbox") {
        return (
            <input
                type="checkbox"
                className={cn(
                    "h-5 w-5 rounded-md border-2 border-border bg-input text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // Modern radio button styling
    if (type === "radio") {
        return (
            <input
                type="radio"
                className={cn(
                    "h-5 w-5 rounded-full border-2 border-border bg-input text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200",
                    className
                )}
                ref={ref}
                id={inputId}
                {...props}
            />
        );
    }

    // For regular inputs with enhanced modern wrapper structure
    return (
        <div className="space-y-3">
            {label && (
                <label
                    htmlFor={inputId}
                    className={cn(
                        "text-sm font-semibold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 tracking-tight",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                <input
                    type={type}
                    className={cn(
                        baseInputClasses,
                        error && "border-destructive focus-visible:ring-destructive focus-visible:border-destructive",
                        className
                    )}
                    ref={ref}
                    id={inputId}
                    {...props}
                />
                
                {/* Enhanced focus indicator */}
                <div className="absolute inset-0 rounded-xl ring-1 ring-transparent peer-focus-visible:ring-primary/20 pointer-events-none transition-all duration-200" />
            </div>

            {description && !error && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                    {description}
                </p>
            )}

            {error && (
                <p className="text-sm text-destructive font-medium leading-relaxed flex items-center gap-1">
                    <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                </p>
            )}
        </div>
    );
});

Input.displayName = "Input";

export default Input;